import { useQueries } from '@tanstack/react-query';
import dayjs from 'dayjs';
import objectHash from 'object-hash';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from 'react';

import { Dref, EGfsLevel, Fhr, Tref } from '#entities';

import {
  WindsForecastGridSearchParams,
  windsForecastGridPngQueryFn,
  windsForecastGridPngQueryKey,
} from '../../../api';
import { GridStatus, useGrid } from '../hooks';

import type { CacheStats, CacheStatus } from '../types';

export interface WindState {
  status: GridStatus;
  width: number;
  height: number;
  data: Blob | null;
  maxWindSpeed: number;
  progress?: number;
  progressMessage?: string;
}

export interface WindDataContextProps {
  windState: WindState;
  windParamsHash: string;

  windParamsSet: WindsForecastGridSearchParams[];
  windParams: WindsForecastGridSearchParams;
  setWindParams: Dispatch<SetStateAction<WindsForecastGridSearchParams>>;

  preload: boolean;
  setPreload: Dispatch<SetStateAction<boolean>>;
  cacheStats: CacheStats;
}

const defaultLevel = EGfsLevel.lev_10_m_above_ground;

export const WindDataContext = createContext<WindDataContextProps | undefined>(
  undefined,
);

export const useWindData = () => {
  const context = useContext(WindDataContext);
  if (!context) {
    throw new Error(
      'useWindDataContext must be used within a WindDataProvider',
    );
  }
  return context;
};

export const WindDataProvider = ({ children }: PropsWithChildren) => {
  const [actualDateTime, setActualDateTime] = useState(
    dayjs.utc().subtract(30, 'minutes').startOf('hour'),
  );
  const [windParamsSet, setWindParamsSet] = useState<
    WindsForecastGridSearchParams[]
  >(
    Array.from({ length: 13 }, (_, index) => {
      const dateTime = actualDateTime.clone().add(index, 'hours');
      return {
        dateTime,
        dref: actualDateTime.format('YYYYMMDD') as Dref,
        level: defaultLevel,
        fhr: index.toString().padStart(2, '0') as Fhr,
        tref: actualDateTime.format('HH') as Tref,
      };
    }),
  );

  const [preload, setPreload] = useState(false);

  // Prefetch all forecast hours when preload is active (play mode)
  const prefetchResults = useQueries({
    queries: preload
      ? windParamsSet.map(params => ({
          queryKey: windsForecastGridPngQueryKey(params),
          queryFn: () => windsForecastGridPngQueryFn(params),
          staleTime: 60 * 60 * 1000,
        }))
      : [],
  });

  // Derive cacheStats from React Query prefetch state
  const cacheStats = useMemo((): CacheStats => {
    if (!preload || prefetchResults.length === 0) {
      return {
        success: 0,
        pending: 0,
        error: 0,
        idle: 0,
        total: 0,
        progress: 100,
        status: 'success' as CacheStatus,
      };
    }
    const counts: Record<CacheStatus, number> = {
      success: 0,
      pending: 0,
      error: 0,
      idle: 0,
    };
    for (const q of prefetchResults) {
      counts[q.status] = (counts[q.status] || 0) + 1;
    }
    const total = prefetchResults.length;
    const progress = Math.round((counts.success / total) * 100);
    let status: CacheStatus;
    if (counts.error > 0) {
      status = 'error';
    } else if (counts.pending > 0) {
      status = 'pending';
    } else {
      status = 'success';
    }
    return { ...counts, total, progress, status };
  }, [preload, prefetchResults]);

  // Hourly refresh — update wind params when the reference hour changes
  useEffect(() => {
    const interval = setInterval(() => {
      const newActualDateTime = dayjs
        .utc()
        .subtract(30, 'minutes')
        .startOf('hour');
      if (!actualDateTime.isSame(newActualDateTime)) {
        setActualDateTime(newActualDateTime);
        setWindParamsSet(
          Array.from({ length: 13 }, (_, index) => {
            const dateTime = newActualDateTime.clone().add(index, 'hours');
            return {
              dateTime,
              dref: newActualDateTime.format('YYYYMMDD') as Dref,
              level: defaultLevel,
              fhr: index.toString().padStart(2, '0') as Fhr,
              tref: newActualDateTime.format('HH') as Tref,
            };
          }),
        );
      }
    }, 1_000 * 60);
    return () => clearInterval(interval);
  }, [actualDateTime]);

  const [windParams, setWindParams] = useState<WindsForecastGridSearchParams>(
    windParamsSet[1],
  );

  useEffect(() => {
    setWindParams(windParamsSet[1]);
  }, [windParamsSet]);

  const windParamsHash = useMemo(() => objectHash(windParams), [windParams]);

  const { status, data, height, width, maxWindSpeed } = useGrid(windParams);

  const ctx = useMemo(
    (): WindDataContextProps => ({
      windState: {
        status,
        data,
        height,
        width,
        maxWindSpeed,
      },
      windParamsHash,
      windParamsSet,
      windParams,
      setWindParams,
      preload,
      setPreload,
      cacheStats,
    }),
    [
      status,
      data,
      height,
      width,
      maxWindSpeed,
      windParamsHash,
      windParamsSet,
      windParams,
      preload,
      cacheStats,
    ],
  );

  return (
    <WindDataContext.Provider value={ctx}>{children}</WindDataContext.Provider>
  );
};
